// src/hooks/useForceLogout.ts
// Hook for handling SSO force logout via WebSocket, and for signing this tab
// out when the session is ended on purpose in another tab.

import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authSocketService, ForceLogoutPayload } from '../services/authSocketService';
import { clearAuthState, getAuthToken } from '../services/authService';
import {
  LogoutReason,
  broadcastLogout,
  isAuthChannelSupported,
  onLogoutBroadcast,
  reportSessionEnd
} from '../services/sessionEvents';

interface UseForceLogoutOptions {
  /** Callback to clear user state in the app */
  onLogout?: () => void;
  /** Whether the user is currently authenticated */
  isAuthenticated: boolean;
  /** The signed-in user's id, to notice when someone else signs in on this browser */
  userId?: string;
}

/**
 * Hook that connects to the auth WebSocket and handles force logout events.
 *
 * When an IdP-initiated SLO occurs:
 * 1. IdP sends LogoutRequest to /auth/saml/logout
 * 2. Backend revokes tokens + broadcasts force_logout via PubSub
 * 3. This hook receives the WebSocket event
 * 4. App clears tokens and redirects to login, and tells the other tabs
 *
 * Other tabs are signed out only by a deliberate logout (the user's, or the
 * server's), announced over the auth BroadcastChannel. A tab that ran into a
 * problem on its own clears the shared storage but no longer takes the other
 * tabs down with it.
 */
export function useForceLogout({
  onLogout,
  isAuthenticated,
  userId
}: UseForceLogoutOptions): void {
  const navigate = useNavigate();
  const isHandlingLogoutRef = useRef(false);

  const signOutTab = useCallback(
    (reason: LogoutReason, message: string) => {
      // Prevent multiple logout handlers from running
      if (isHandlingLogoutRef.current) {
        return;
      }
      isHandlingLogoutRef.current = true;

      console.log('[useForceLogout] Signing out this tab:', reason);

      // Report first: it reads the access token that clearAuthState removes.
      reportSessionEnd(reason);

      // Disconnect the socket first
      authSocketService.disconnect();

      // Clear all auth state (cookies, localStorage, axios headers)
      clearAuthState();

      // Call the app's logout callback if provided
      if (onLogout) {
        onLogout();
      }

      // Redirect to login with a message
      navigate('/login', {
        replace: true,
        state: {
          message,
          fromForceLogout: true
        }
      });

      // Reset the flag after a short delay
      setTimeout(() => {
        isHandlingLogoutRef.current = false;
      }, 1000);
    },
    [navigate, onLogout]
  );

  const handleForceLogout = useCallback(
    (payload: ForceLogoutPayload) => {
      if (payload.reason === 'socket_unauthorized') {
        // The socket refused this tab's token; other tabs check their own.
        signOutTab('socket_unauthorized', getLogoutReasonMessage('session_expired'));
        return;
      }
      // The server ended the session (IdP logout, admin action): every tab goes.
      broadcastLogout('server_force_logout');
      signOutTab('server_force_logout', getLogoutReasonMessage(payload.reason));
    },
    [signOutTab]
  );

  useEffect(() => {
    // Only connect when authenticated and we have a token
    const token = getAuthToken();
    if (!isAuthenticated || !token) {
      // Ensure we're disconnected when not authenticated
      authSocketService.disconnect();
      return;
    }

    // Connect to the auth socket
    authSocketService.connect(handleForceLogout);

    // Cleanup on unmount or when auth state changes
    return () => {
      authSocketService.disconnect();
    };
  }, [isAuthenticated, handleForceLogout]);

  // A deliberate logout in another tab signs this tab out too.
  useEffect(() => {
    if (!isAuthenticated) return;
    return onLogoutBroadcast(() => {
      signOutTab(
        'logged_out_in_another_tab',
        getLogoutReasonMessage('logged_out_in_another_tab')
      );
    });
  }, [isAuthenticated, signOutTab]);

  // Watch the stored user for someone else signing in on this browser, so this
  // tab doesn't keep showing the previous user's screen.
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== 'user' || !isAuthenticated) return;

      if (event.newValue === null) {
        // Without BroadcastChannel this is the only signal of a logout in
        // another tab, so keep the old behaviour there.
        if (!isAuthChannelSupported()) {
          signOutTab(
            'logged_out_in_another_tab',
            getLogoutReasonMessage('logged_out_in_another_tab')
          );
        }
        return;
      }

      if (userId !== undefined && storedUserId(event.newValue) !== String(userId)) {
        // Reload as whoever is signed in now.
        reportSessionEnd('different_user_signed_in');
        authSocketService.disconnect();
        window.location.assign('/');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [isAuthenticated, userId, signOutTab]);
}

function storedUserId(storedUser: string): string | null {
  try {
    const id = JSON.parse(storedUser)?.id;
    return id === undefined || id === null ? null : String(id);
  } catch {
    return null;
  }
}

/**
 * Get a user-friendly message for the logout reason
 */
function getLogoutReasonMessage(reason: string): string {
  switch (reason) {
    case 'idp_logout':
    case 'slo_request':
      return 'You have been signed out by your organization.';
    case 'session_expired':
      return 'Your session has expired. Please sign in again.';
    case 'token_revoked':
      return 'Your session was ended. Please sign in again.';
    case 'logged_out_in_another_tab':
      return 'You were signed out in another tab.';
    case 'admin_action':
      return 'Your session was ended by an administrator.';
    default:
      return 'You have been signed out. Please sign in again.';
  }
}

export default useForceLogout;
